import type { TChatCitation, TChatCitationImage } from "@roko/ui/components/chat/types";

/** Resolve API-relative media paths against the configured API base URL. */
export const resolveMediaUrl = (apiBaseUrl: string, pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const base = apiBaseUrl.replace(/\/$/, "");
  return `${base}/${pathOrUrl.replace(/^\//, "")}`;
};

/** Map raw citation.images JSON into chat image models. */
export const mapRawCitationImages = (
  apiBaseUrl: string,
  raw: unknown,
): TChatCitationImage[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }

  const images: TChatCitationImage[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Record<string, unknown>;
    const path = typeof row.url === "string" ? row.url : null;
    if (!path) {
      continue;
    }
    // Only allow relative media paths or same-origin absolute URLs from our API.
    if (/^https?:\/\//i.test(path)) {
      const baseHost = (() => {
        try {
          return new URL(apiBaseUrl).host;
        } catch {
          return "";
        }
      })();
      try {
        if (new URL(path).host !== baseHost) {
          continue;
        }
      } catch {
        continue;
      }
    } else if (path.includes("..") || !path.replace(/^\//, "").startsWith("media/")) {
      continue;
    }

    images.push({
      url: resolveMediaUrl(apiBaseUrl, path),
      alt: typeof row.alt === "string" && row.alt ? row.alt : "Document figure",
      caption: typeof row.caption === "string" ? row.caption : null,
      page: typeof row.page === "number" ? row.page : null,
      figureId: typeof row.figure_id === "string" ? row.figure_id : null,
    });
  }

  return images.length > 0 ? images : undefined;
};

export type TRawCitationLike = {
  title?: unknown;
  doc_number?: unknown;
  page?: unknown;
  url?: unknown;
  images?: unknown;
};

/** Map Bedrock/API citation rows into chat citation models. */
export const mapRawCitations = (
  apiBaseUrl: string,
  citations: TRawCitationLike[] | null | undefined,
): TChatCitation[] | undefined => {
  if (!citations?.length) {
    return undefined;
  }

  return citations.map((citation) => {
    const row = citation as Record<string, unknown>;
    return {
      title: String(row.title ?? "Source"),
      docNumber: typeof row.doc_number === "string" ? row.doc_number : null,
      page: typeof row.page === "number" ? row.page : null,
      url: typeof row.url === "string" ? row.url : null,
      images: mapRawCitationImages(apiBaseUrl, row.images),
    };
  });
};

/** Flatten citation images for message-level display, preferring source metadata. */
export const collectMessageImages = (
  citations: TChatCitation[] | undefined,
): TChatCitationImage[] | undefined => {
  if (!citations?.length) {
    return undefined;
  }
  const seen = new Set<string>();
  const images: TChatCitationImage[] = [];
  for (const citation of citations) {
    for (const image of citation.images ?? []) {
      if (seen.has(image.url)) {
        continue;
      }
      seen.add(image.url);
      images.push({
        ...image,
        sourceTitle: citation.title,
        docNumber: citation.docNumber ?? null,
        page: image.page ?? citation.page ?? null,
      });
    }
  }
  return images.length > 0 ? images : undefined;
};

/** Caption under chat/report figures: doc # · title · p. N */
export const formatImageSourceLabel = (
  image: Pick<TChatCitationImage, "docNumber" | "sourceTitle" | "page" | "caption" | "alt">,
  pageLabel = "p.",
): string => {
  const parts: string[] = [];
  if (image.docNumber) {
    parts.push(image.docNumber);
  }
  if (image.sourceTitle) {
    parts.push(image.sourceTitle);
  }
  if (image.page != null) {
    parts.push(`${pageLabel} ${image.page}`);
  }
  if (parts.length > 0) {
    return parts.join(" · ");
  }
  const fallback = image.caption || image.alt;
  if (!fallback) {
    return image.page != null ? `${pageLabel} ${image.page}` : "";
  }
  return image.page != null ? `${fallback} · ${pageLabel} ${image.page}` : fallback;
};
